import type { FC, ReactNode } from 'react';
import { Suspense } from 'react';

import { Box, Divider, ErrorBoundary, Stack, useDeviceType, useUrl } from '@packages/shared';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { ProductListPageLayoutFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListPageLayoutFragmentFragmentDoc';

import { usePaging } from '../../hooks/usePaging';
import { InfiniteProductGrid } from '../InfiniteProductGrid';
import { ProductGrid } from '../ProductGrid';
import { ProductListPagination } from '../ProductListPagination';
import type { FilterSettingsProps } from '../FilterSettings';
import { FilterSettings } from '../FilterSettings';
import { SearchAdServerIntegration } from '../SearchAdServerIntegration';
import { DisplaySettingsDesktop } from '../DisplaySettingsDesktop';
import { DisplaySettingsMobile } from '../DisplaySettingsMobile';
import { SearchStructuredData } from '../SearchStructuredData';

/* GraphQL */ `
  fragment ProductListPageLayoutFragment on SearchProductResult {
    ...FilterSettingsFragment
    ...UsePagingFragment
    ...DisplaySettingsDesktopFragment
    ...DisplaySettingsMobileFragment
    ...ProductListPaginationFragment
    ...ProductGridFragment
    ...InfiniteProductGridFragment
    ...SearchStructuredDataFragment
    summary {
      dominantProductGroup
    }

    ... on SuccessfulSearchResult {
      recoveryStrategy {
        ... on FilterAdjustment {
          removeFilters
        }
      }
    }
  }
`;

export type ProductListPageLayoutProps = {
  maskedData: FragmentType<typeof ProductListPageLayoutFragmentFragmentDoc>;
  topLevelCategories?: string[];
  aboveMainContentComponent: ReactNode;
  leftSidebarDesktopComponent: ReactNode;
  renderCategoryDialogContent: FilterSettingsProps['renderCategoryDialogContent'];
  belowProductsComponent: ReactNode;
  referrer?: string;
};

/**
 * Shared layout for pages displaying product lists
 *
 * Includes the actual product grid, filtering, sorting, display mode, pagination, and slots for additional content.
 */
export const ProductListPageLayout: FC<ProductListPageLayoutProps> = ({
  maskedData,
  topLevelCategories,
  aboveMainContentComponent,
  leftSidebarDesktopComponent,
  renderCategoryDialogContent,
  belowProductsComponent,
  referrer,
}) => {
  const data = getFragmentData(ProductListPageLayoutFragmentFragmentDoc, maskedData);

  const { isMobile, isDesktop } = useDeviceType();
  const url = useUrl();

  const { pagingMode, pageCount, currentPage } = usePaging(data);

  const {
    summary: { dominantProductGroup },
  } = data;

  const removeFilters =
    data.__typename === 'SuccessfulSearchResult' &&
    data.recoveryStrategy?.__typename === 'FilterAdjustment' &&
    data.recoveryStrategy.removeFilters;

  return (
    // position relative is necessary for the SearchAdServerIntegration component
    <Stack gap={1} padding={1} position="relative">
      <SearchStructuredData maskedData={data} />

      {aboveMainContentComponent}

      <FilterSettings
        maskedData={data}
        showRemovedFiltersNotification={removeFilters}
        renderCategoryDialogContent={renderCategoryDialogContent}
      />

      {isDesktop ? (
        <DisplaySettingsDesktop maskedData={data} />
      ) : (
        <DisplaySettingsMobile maskedData={data} />
      )}

      <Divider />

      <Stack direction="row" gap={1}>
        {!isMobile && (
          <Box sx={{ width: { md: '25%', lg: '20%' } }}>{leftSidebarDesktopComponent}</Box>
        )}

        <Stack
          gap={2}
          sx={{
            flex: 1,
            // NOTE: minWidth is required to properly propagate size constraints to RecoSlider and prevent the page from blowing up
            // see also: https://github.com/philipwalton/flexbugs#1-minimum-content-sizing-of-flex-items-not-honored
            minWidth: 0,
          }}
        >
          {pagingMode === 'infinite' ? (
            <InfiniteProductGrid
              maskedResult={data}
              key={url}
              referrer={referrer}
              topLevelCategories={topLevelCategories}
            />
          ) : (
            <ProductGrid
              maskedResult={data}
              key={url}
              pageNumber={currentPage}
              topLevelCategories={topLevelCategories}
            />
          )}
          {pageCount > 1 && pagingMode === 'pagination' && (
            <Box
              sx={{
                alignSelf: 'center',
              }}
            >
              <ProductListPagination maskedData={data} />
            </Box>
          )}
          {belowProductsComponent}
        </Stack>
      </Stack>

      <ErrorBoundary>
        <Suspense>
          <SearchAdServerIntegration majorMkz={dominantProductGroup ?? undefined} />
        </Suspense>
      </ErrorBoundary>
    </Stack>
  );
};
